import { Copy, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import {
  ReturnResolutionInput,
  type Order,
  type ReturnDetail,
  type ReturnResolvedStoreCredit,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useResolveReturn } from '../hooks/use-return-mutations';
import {
  resolveSchema,
  toResolveBody,
  type ResolveFormInput,
  type ResolveFormOutput,
} from '../schemas/resolve-schema';
import {
  defaultResolveAmount,
  isMoneyResolution,
  returnedValue,
  suggestedRestockingFee,
} from '../schemas/return-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'resolution',
  'amount',
  'restockingFee',
  'restock',
  'note',
]);
const mapField = (field: string) => field.replace(/\.(currency|amount)$/, '');

function StoreCreditPanel({
  credit,
  onDone,
}: {
  credit: NonNullable<ReturnResolvedStoreCredit>;
  onDone: () => void;
}) {
  const { t } = useTranslation('returns');
  const format = useFormat();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(credit.code);
      toast.success(t('storeCredit.copied'));
    } catch {
      toast.error(t('storeCredit.copyFailed'));
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm">{t('storeCredit.summary', { amount: format.money(credit.amount) })}</p>
      <div className="flex items-center gap-2">
        <code
          className="bg-muted flex-1 rounded-md px-3 py-2 text-center font-mono text-lg tracking-widest select-all"
          data-testid="store-credit-code"
        >
          {credit.code}
        </code>
        <Button type="button" variant="outline" onClick={() => void copy()}>
          <Copy aria-hidden />
          {t('storeCredit.copy')}
        </Button>
      </div>
      <Alert>
        <TriangleAlert aria-hidden />
        <AlertTitle>{t('storeCredit.warningTitle')}</AlertTitle>
        <AlertDescription>{t('storeCredit.warning')}</AlertDescription>
      </Alert>
      <DialogFooter>
        <Button type="button" onClick={onDone}>
          {t('storeCredit.done')}
        </Button>
      </DialogFooter>
    </div>
  );
}

export interface ResolveDialogProps {
  storeId: string;
  ret: ReturnDetail;
  order: Order;
  /** `settings.returns.restockingFeePercent` of the store; a suggestion staff confirm here. */
  feePercent: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Settles an inspected return. Refund records money back on the order, store credit issues a gift
 * card whose code is shown once here; exchange, repair and resize are recorded for follow-up.
 * Only resolutions the member may record are offered; amount and fee stay within the returned
 * value, as the backend requires.
 */
export function ResolveDialog({
  storeId,
  ret,
  order,
  feePercent,
  open,
  onOpenChange,
}: ResolveDialogProps) {
  const { t } = useTranslation(['returns', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const resolve = useResolveReturn(storeId);
  // Held in component state only; closing the dialog drops the code for good.
  const [credit, setCredit] = useState<ReturnResolvedStoreCredit>(null);
  const value = useMemo(() => returnedValue(ret, order), [ret, order]);
  // The backend lists what this member may record (refund needs orders.refund, store credit
  // gift_cards.write); the dialog offers exactly those.
  const options = Object.values(ReturnResolutionInput).filter((r) =>
    ret.allowedResolutions.includes(r),
  );
  const defaults = useMemo((): ResolveFormInput => {
    const fee = suggestedRestockingFee(value, feePercent);
    const preset = ret.resolution && options.includes(ret.resolution) ? ret.resolution : null;
    return {
      resolution: preset ?? options[0] ?? 'exchange',
      amount: defaultResolveAmount(value, fee),
      restockingFee: fee,
      restock: ret.lines.some((line) => line.condition !== 'damaged'),
      note: '',
    };
    // `options` derives from permissions, which do not change while the dialog is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, feePercent, ret]);
  const schema = useMemo(() => resolveSchema(value), [value]);
  const form = useForm<ResolveFormInput, unknown, ResolveFormOutput>({
    resolver: useI18nResolver(schema),
    defaultValues: defaults,
  });
  const resolution = form.watch('resolution');
  const movesMoney = isMoneyResolution(resolution);

  const close = () => {
    onOpenChange(false);
    setCredit(null);
    resolve.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const resolved = await resolve.mutateAsync({ id: ret.id, body: toResolveBody(values) });
      if (resolved.storeCredit) setCredit(resolved.storeCredit);
      else close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent
        className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg"
        onInteractOutside={(e) => {
          if (credit) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {credit
              ? t('returns:storeCredit.title')
              : t('returns:resolve.title', { rma: ret.rmaNumber })}
          </DialogTitle>
          <DialogDescription>
            {credit ? t('returns:storeCredit.description') : t('returns:resolve.description')}
          </DialogDescription>
        </DialogHeader>
        {credit ? (
          <StoreCreditPanel credit={credit} onDone={close} />
        ) : (
          <Form {...form}>
            <form onSubmit={submit} noValidate className="flex flex-col gap-4">
              {form.formState.errors.root?.server?.message ? (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
                </Alert>
              ) : null}
              <p className="text-sm tabular-nums" data-testid="returned-value">
                {t('returns:resolve.value', { amount: format.money(value) })}
              </p>
              <FormField
                control={form.control}
                name="resolution"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('returns:resolve.resolution')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="grid gap-2"
                      >
                        {options.map((option) => (
                          <label
                            key={option}
                            htmlFor={`resolution-${option}`}
                            className={cn(
                              'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                              field.value === option && 'border-primary bg-primary-tint/40',
                            )}
                          >
                            <RadioGroupItem
                              id={`resolution-${option}`}
                              value={option}
                              className="mt-0.5"
                            />
                            <span className="grid gap-0.5 text-sm">
                              <span className="font-medium">
                                {t(`returns:resolution.${option}`)}
                              </span>
                              <span className="text-muted-foreground text-xs">
                                {t(`returns:resolutionHint.${option}`)}
                              </span>
                            </span>
                          </label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    {options.length < Object.values(ReturnResolutionInput).length ? (
                      <FormDescription>{t('returns:resolve.hiddenResolutions')}</FormDescription>
                    ) : null}
                    <FormMessage />
                  </FormItem>
                )}
              />
              {movesMoney ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="restockingFee"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{t('returns:resolve.fee')}</FormLabel>
                        <FormControl>
                          <MoneyInput
                            currency={order.currency}
                            value={field.value}
                            onChange={(next) => {
                              field.onChange(next);
                              // The amount follows the fee until staff type their own.
                              if (!form.getFieldState('amount').isDirty) {
                                form.setValue('amount', defaultResolveAmount(value, next));
                              }
                            }}
                            onBlur={field.onBlur}
                            aria-invalid={fieldState.invalid}
                          />
                        </FormControl>
                        <FormDescription>
                          {t('returns:resolve.feeHint', { percent: feePercent })}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>
                          {resolution === 'refund'
                            ? t('returns:resolve.refundAmount')
                            : t('returns:resolve.creditAmount')}
                        </FormLabel>
                        <FormControl>
                          <MoneyInput
                            currency={order.currency}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            aria-invalid={fieldState.invalid}
                          />
                        </FormControl>
                        <FormDescription>{t('returns:resolve.amountHint')}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : null}
              <FormField
                control={form.control}
                name="restock"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                    <div className="grid gap-1">
                      <FormLabel>{t('returns:resolve.restock')}</FormLabel>
                      <FormDescription>{t('returns:resolve.restockHint')}</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('returns:resolve.note')}</FormLabel>
                    <FormControl>
                      <Textarea rows={2} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={close}>
                  {t('common:actions.cancel')}
                </Button>
                <Button type="submit" disabled={resolve.isPending}>
                  {resolve.isPending
                    ? t('returns:resolve.submitting')
                    : t('returns:resolve.submit')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
